import React, { useEffect, useRef, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import packageInfo from '../../../package.json';

import logo from '../../assets/icons/logo.svg';
import { ROUTES } from '../../AppRouting';
import { RootState } from '../../store/store';
import { getCurrentPath } from '../utils';
import {
  signOut,
  toggleOnboardingModal,
  userClickedSupport,
} from '../../store/auth/auth.actions';
import { SupportIcon } from '../Icons/SupportIcon';
import { resetEngagementPageNumber } from '../../store/deals/deals.actions';
import { PopupOption } from '../Popup/PopupOption';
import { SIGNUP_LINK } from '../../constants/config';
import { AccountIcon } from './assets/AccountIcon';
import { SignOutIcon } from './assets/SignOutIcon';
import { HelpIcon } from './assets/HelpIcon';
import { Avatar } from '../Avatar/Avatar';
import { Popup } from '../Popup/Popup';
import { TutorialsIcon } from './assets/TutorialsIcon';
import { ReqFeauturesIcon } from './assets/ReqFeauturesIcon';
import { ContactUsIcon } from './assets/ContactUsIcon';
import { Role } from '../../types';
import { ServiceStatusIcon } from './assets/ServiceStatusIcon';
import { AccountSettingsIcon } from './assets/AccountSettingsIcon';
import { OnboardingIcon } from './assets/OnboardingIcon';

export const Sidebar: React.FC = (): JSX.Element => {
  const { user, org } = useSelector((state: RootState) => state.auth);

  const { settings, status } = useSelector(
    (state: RootState) => state.org.webhook
  );

  const { pathname } = useLocation();

  const history = useHistory();

  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);

  const { shutdown } = useIntercom();

  const currentRoute = ROUTES.find(
    ({ path }) => getCurrentPath(path, user) === pathname
  );

  const avatar = useRef<HTMLDivElement>(null);

  const { version } = packageInfo;

  const clickSupport = () => {
    if (user) {
      dispatch(userClickedSupport(user.userId, user.orgId));
      window.open('https://help.substrata.me/en/', '_blank');
    }
  };

  const onMyDealsClick = () => {
    if (!currentRoute?.path?.includes('engagements')) {
      dispatch(resetEngagementPageNumber());
    }
  };

  const onAccountClick = () => {
    history.push(`/orgs/${user?.orgId}/users/${user?.userId}`);
  };

  const onAccountSettingsClick = () => {
    history.push(`/orgs/${user?.orgId}/admin-settings`);
  };

  const onLogoClick = () => {
    history.push(`/orgs/${user?.orgId}/users/${user?.userId}/engagements`);
    onMyDealsClick();
  };

  const onHelpClick = () => {
    if (user) {
      dispatch(userClickedSupport(user.userId, user.orgId));
      window.open('https://help.substrata.me/en/', '_blank');
    }
  };

  const serviceStatusClick = () => {
    if (user) {
      window.open('https://substrata.statuspage.io/', '_blank');
    }
  };

  const onTutorialsClick = () => {
    window.open(
      'https://help.substrata.me/en/collections/3602298-tutorials',
      '_blank'
    );
  };

  const onOnboardingClick = () => {
    dispatch(toggleOnboardingModal(true));
  };

  const onRequestFeaturesClick = () => {
    window.open('https://feedback.substrata.me', '_blank');
  };

  const onContactUsClick = () => {
    window.open('https://www.substrata.me/contact/', '_blank');
  };

  const onSignOutClick = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    localStorage.removeItem('tokenExpiresAt');
    shutdown();
    dispatch(signOut());
    window.location.href = `${SIGNUP_LINK}/logout`;
  };

  const isNotIframeCheck = !pathname.includes('personalq_competence');

  const isNavigationDisabledCheck =
    typeof org?.isFunctionalityEnabled === 'boolean' &&
    !org.isFunctionalityEnabled;

  const appSumoPermissionCheck =
    org?.acquisitionSource &&
    org.acquisitionSource === 'AppSumo' &&
    user?.role === 'owner';

  const isAccountOptionDisabled = currentRoute?.label
    ? currentRoute.label === 'Account'
    : false;

  const isAdminSettingDisabled = currentRoute?.label
    ? pathname.includes('/admin-settings')
    : false;

  const options: JSX.Element[] = [
    <PopupOption
      key="My Account"
      text="My Account"
      onChoose={onAccountClick}
      className="sidebar__user-popup__option popup__option"
      Icon={AccountIcon}
      disabled={isAccountOptionDisabled || isNavigationDisabledCheck}
      isSidebar
    />,
    <PopupOption
      key="Admin Settings"
      text="Admin Settings"
      onChoose={onAccountSettingsClick}
      className="sidebar__user-popup__option popup__option"
      Icon={AccountSettingsIcon}
      disabled={isAdminSettingDisabled}
      isSidebar
      hasBorderBottom
      hidden={user?.role === 'dealmaker'}
    />,
    <PopupOption
      key="Help Center"
      text="Help Center"
      onChoose={onHelpClick}
      className="sidebar__user-popup__option popup__option"
      Icon={HelpIcon}
      isSidebar
    />,
    <PopupOption
      key="Tutorials"
      text="Tutorials"
      onChoose={onTutorialsClick}
      className="sidebar__user-popup__option popup__option"
      Icon={TutorialsIcon}
      isSidebar
    />,
    <PopupOption
      key="Get Started"
      text="Get Started"
      onChoose={onOnboardingClick}
      className="sidebar__user-popup__option popup__option"
      Icon={OnboardingIcon}
      isSidebar
      hasBorderBottom
    />,
    <PopupOption
      key="Request Features"
      text="Request Features"
      onChoose={onRequestFeaturesClick}
      className="sidebar__user-popup__option popup__option"
      Icon={ReqFeauturesIcon}
      isSidebar
      disabled={isNavigationDisabledCheck}
    />,
    <PopupOption
      key="Contact Us"
      text="Contact Us"
      onChoose={onContactUsClick}
      className="sidebar__user-popup__option popup__option"
      Icon={ContactUsIcon}
      isSidebar
      hasBorderBottom
    />,
    <PopupOption
      key="Service Status"
      text="Service Status"
      onChoose={serviceStatusClick}
      className="sidebar__user-popup__option popup__option"
      Icon={ServiceStatusIcon}
      isSidebar
    />,
    <PopupOption
      key="Sign out"
      text="Sign out"
      onChoose={onSignOutClick}
      className="sidebar__user-popup__option popup__option"
      Icon={SignOutIcon}
      isSidebar
      isRed
    />,
  ];

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    const isAvatarTarget = avatar.current?.contains(target);
    if (!isAvatarTarget && open) setOpen(false);
    else if (isAvatarTarget) setOpen(!open);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () =>
      document.removeEventListener('click', handleClickOutside, true);
  });

  const defineRouteStatus = (
    path: string,
    disabled: boolean,
    label: string
  ) => {
    let active = pathname === getCurrentPath(path, user);

    if (pathname.includes('/team-analytics/')) {
      active =
        pathname.split('/').slice(0, -1).join('/') ===
        getCurrentPath(path, user).split('/').slice(0, -1).join('/');
    }

    const error =
      label === 'Admin Settings' &&
      (status.connectionLost ||
        status.updateFailed ||
        settings.isDisabledByError);

    const isNavigationDisabled =
      isNavigationDisabledCheck &&
      !active &&
      !path.includes('admin-settings/billing');

    const currentClassName = `${error ? 'error' : ''} sidebar__nav-link ${
      pathname === getCurrentPath(path, user) ? 'active' : ''
    } ${disabled || isNavigationDisabled ? 'disabled' : ''} ${
      path.includes('admin-settings') ? 'base-opacity' : ''
    } `;

    return { active, currentClassName, error };
  };

  const mappedRoutes = ROUTES.map(
    ({ path, label, tooltip, Icon, roles, disabled }, i) => {
      const { active: isActive, currentClassName, error } = defineRouteStatus(
        path,
        disabled || false,
        label
      );

      return (
        <React.Fragment key={`${label}${path}${i}`}>
          {roles.includes(user?.role as Role) && Icon ? (
            <>
              {path.includes('billing') && !appSumoPermissionCheck ? null : (
                <Link
                  className={currentClassName}
                  to={disabled ? pathname : getCurrentPath(path, user)}
                  onClick={
                    path.includes('/engagements') ? onMyDealsClick : () => {}
                  }
                >
                  <Icon
                    disabled={disabled}
                    className="sidebar__nav-link__icon"
                    active={isActive}
                    tooltip={tooltip}
                    isError={error}
                  />
                </Link>
              )}
            </>
          ) : null}
        </React.Fragment>
      );
    }
  );

  const avatarSection = (
    <div className="sidebar__user">
      <div ref={avatar} onMouseEnter={() => setOpen(true)}>
        <Avatar
          user={user}
          image={user?.avatar}
          className="sidebar__user-avatar"
          inSidebar
        />
      </div>
      <Popup
        open={open}
        className="sidebar__user-popup"
        options={options}
        onMouseOut={() => setOpen(false)}
      />
    </div>
  );

  const defineSidebarClassNames = () => {
    const isInIframe = isNotIframeCheck ? '' : 'hidden';

    const isNavigationDisabled = isNavigationDisabledCheck
      ? 'disabled-navigation'
      : '';

    const wrapperClassName = `${isInIframe} ${isNavigationDisabled} sidebar`;

    const logoClassName = `${isNavigationDisabled} sidebar__top`;

    const navSectionClassName = `${isNavigationDisabled} sidebar__nav`;

    return { wrapperClassName, logoClassName, navSectionClassName };
  };

  const {
    wrapperClassName,
    logoClassName,
    navSectionClassName,
  } = defineSidebarClassNames();

  return (
    <aside className={wrapperClassName}>
      <div className={logoClassName}>
        <img
          src={logo}
          alt="Logo"
          className="sidebar__logo"
          onClick={onLogoClick}
        />
      </div>
      <nav className={navSectionClassName}>{mappedRoutes}</nav>
      <div className="sidebar-support" onClick={clickSupport}>
        <SupportIcon />
      </div>
      <div className="sidebar-version">
        <span>Ver. {version}</span>
      </div>
      {avatarSection}
    </aside>
  );
};
